import * as THREE from 'three';

const orbCanvas = document.getElementById('orb-canvas');

if (orbCanvas) {
    // Scene setup
    const scene = new THREE.Scene();
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Camera setup - slightly further back for the field view
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 2.0;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    orbCanvas.innerHTML = '';
    orbCanvas.appendChild(renderer.domElement);

    // --- SHADERS ---

    // Vertex Shader: Implements Curl Noise for fluid motion
    // We use a 3D Simplex Noise derivative function to calculate curl.
    const vertexShader = `
        uniform float uTime;
        uniform float uSize;
        uniform float uSpeed;
        uniform float uAmp;
        
        attribute float aScale;
        attribute vec3 aRandom;
        
        varying vec3 vColor;
        varying float vAlpha;

        // Simplex 3D Noise 
        // derived from https://github.com/stegu/webgl-noise
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
            const vec2 C = vec2(1.0/6.0, 1.0/3.0) ;
            const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

            // First corner
            vec3 i  = floor(v + dot(v, C.yyy) );
            vec3 x0 = v - i + dot(i, C.xxx) ;

            // Other corners
            vec3 g = step(x0.yzx, x0.xyz);
            vec3 l = 1.0 - g;
            vec3 i1 = min( g.xyz, l.zxy );
            vec3 i2 = max( g.xyz, l.zxy );

            vec3 x1 = x0 - i1 + C.xxx;
            vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y
            vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y

            // Permutations
            i = mod289(i);
            vec4 p = permute( permute( permute(
                        i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                    + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
                    + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

            // Gradients: 7x7 points over a square, mapped onto an octahedron.
            // The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)
            float n_ = 0.142857142857; // 1.0/7.0
            vec3  ns = n_ * D.wyz - D.xzx;

            vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

            vec4 x_ = floor(j * ns.z);
            vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

            vec4 x = x_ *ns.x + ns.yyyy;
            vec4 y = y_ *ns.x + ns.yyyy;
            vec4 h = 1.0 - abs(x) - abs(y);

            vec4 b0 = vec4( x.xy, y.xy );
            vec4 b1 = vec4( x.zw, y.zw );

            vec4 s0 = floor(b0)*2.0 + 1.0;
            vec4 s1 = floor(b1)*2.0 + 1.0;
            vec4 sh = -step(h, vec4(0.0));

            vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
            vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

            vec3 p0 = vec3(a0.xy,h.x);
            vec3 p1 = vec3(a0.zw,h.y);
            vec3 p2 = vec3(a1.xy,h.z);
            vec3 p3 = vec3(a1.zw,h.w);

            //Normalise gradients
            vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
            p0 *= norm.x;
            p1 *= norm.y;
            p2 *= norm.z;
            p3 *= norm.w;

            // Mix final noise value
            vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
            m = m * m;
            return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1),
                                        dot(p2,x2), dot(p3,x3) ) );
        }

        vec3 curlNoise(vec3 p) {
            const float e = 0.1;
            float n1 = snoise(p + vec3(e, 0, 0));
            
            float n = snoise(p);
            return vec3(n); 
        }
        
        // Classic perlin flow
        vec3 snoiseVec3( vec3 x ){
          float s  = snoise(vec3( x ));
          float s1 = snoise(vec3( x.y - 19.1 , x.z + 33.4 , x.x + 47.2 ));
          float s2 = snoise(vec3( x.z + 74.2 , x.x - 124.5 , x.y + 99.4 ));
          return vec3( s , s1 , s2 );
        }

        vec3 curl( vec3 p ){
          const float e = .1;
          vec3 dx = vec3( e   , 0.0 , 0.0 );
          vec3 dy = vec3( 0.0 , e   , 0.0 );
          vec3 dz = vec3( 0.0 , 0.0 , e   );

          vec3 p_x0 = snoiseVec3( p - dx );
          vec3 p_x1 = snoiseVec3( p + dx );
          vec3 p_y0 = snoiseVec3( p - dy );
          vec3 p_y1 = snoiseVec3( p + dy );
          vec3 p_z0 = snoiseVec3( p - dz );
          vec3 p_z1 = snoiseVec3( p + dz );

          float x = p_y1.z - p_y0.z - p_z1.y + p_z0.y;
          float y = p_z1.x - p_z0.x - p_x1.z + p_x0.z;
          float z = p_x1.y - p_x0.y - p_y1.x + p_y0.x;

          const float divisor = 1.0 / ( 2.0 * e );
          return normalize( vec3( x , y , z ) * divisor );
        }

        void main() {
            vec3 pos = position; // Initial position
            
            // Calculate a flow vector based on Time and Position
            // Slower time = smoother flow
            float time = uTime * uSpeed; 
            
            // Amplitude of the swirl
            float amp = uAmp; 
            
            // Get Curl Noise Vector
            vec3 noiseVelocity = curl(pos * 0.8 + time);
            
            vec3 displaced = pos + noiseVelocity * amp;
            
            // Add some wave layers
            displaced.x += sin(time * 0.5 + pos.y * 2.0) * 0.2;
            displaced.y += cos(time * 0.3 + pos.x * 2.0) * 0.2;

            vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
            
            gl_Position = projectionMatrix * mvPosition;
            
            // Size attenuation
            gl_PointSize = uSize * aScale * (10.0 / -mvPosition.z);
            
            vColor = color;
            // Fade particles based on depth/z-index or randomness
            vAlpha = 0.6 + 0.4 * sin(uTime + aRandom.x * 10.0);
        }
    `;

    const fragmentShader = `
        varying vec3 vColor;
        varying float vAlpha;
        
        void main() {
            // Circle soft glow processing
            float r = distance(gl_PointCoord, vec2(0.5, 0.5));
            if (r > 0.5) discard;
            
            // Smooth edge glow
            float glow = 1.0 - (r * 2.0);
            glow = pow(glow, 1.5); // sharpen the falloff
            
            gl_FragColor = vec4(vColor, vAlpha * glow);
        }
    `;

    // --- GEOMETRY & DATA ---
    const particlesCount = 12000;
    const geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(particlesCount * 3);
    const colors = new Float32Array(particlesCount * 3);
    const scales = new Float32Array(particlesCount);
    const randoms = new Float32Array(particlesCount * 3);

    // Initial Palette
    let currentPalette = [
        new THREE.Color('#C5AB6E'), // Gold
        new THREE.Color('#FFF8E7'), // White/Beige
        new THREE.Color('#4a6fa5'), // Deep Blue
        new THREE.Color('#8aa8d6'), // Light Blue
        new THREE.Color('#785f3a'), // Bronze
    ];

    // State for transition
    const targetColors = new Float32Array(particlesCount * 3);
    let isTransitioningColor = false;
    const lerpSpeed = 0.05;

    // Initialize particles
    for (let i = 0; i < particlesCount; i++) {
        const i3 = i * 3;

        // Spread more horizontally like a galaxy/cloud
        positions[i3] = (Math.random() - 0.5) * 12.0;
        positions[i3 + 1] = (Math.random() - 0.5) * 8.0;
        positions[i3 + 2] = (Math.random() - 0.5) * 5.0;

        // Color
        const color = currentPalette[Math.floor(Math.random() * currentPalette.length)];
        colors[i3] = color.r;
        colors[i3 + 1] = color.g;
        colors[i3 + 2] = color.b;

        // Initialize target colors to same as current
        targetColors[i3] = color.r;
        targetColors[i3 + 1] = color.g;
        targetColors[i3 + 2] = color.b;

        // Scale
        scales[i] = Math.random();

        // Randoms for animation offsets
        randoms[i3] = Math.random();
        randoms[i3 + 1] = Math.random();
        randoms[i3 + 2] = Math.random();
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1));
    geometry.setAttribute('aRandom', new THREE.BufferAttribute(randoms, 3));

    // --- MATERIAL ---
    const material = new THREE.ShaderMaterial({
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        uniforms: {
            uTime: { value: 0 },
            uSize: { value: 20.0 },
            uSpeed: { value: 0.15 },
            uAmp: { value: 0.5 }
        },
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexColors: true
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // --- ANIMATION ---
    const clock = new THREE.Clock();
    let mouseX = 0;
    let mouseY = 0;

    // Interaction
    document.addEventListener('mousemove', (e) => {
        mouseX = e.clientX / width - 0.5;
        mouseY = e.clientY / height - 0.5;
    });

    // Targets for simulation params
    let targetSpeed = 0.15;
    let targetAmp = 0.5;

    const animate = () => {
        const elapsedTime = clock.getElapsedTime();

        material.uniforms.uTime.value = elapsedTime;

        // Lerp Physics
        material.uniforms.uSpeed.value += (targetSpeed - material.uniforms.uSpeed.value) * 0.05;
        material.uniforms.uAmp.value += (targetAmp - material.uniforms.uAmp.value) * 0.05;

        // Lerp Colors
        if (isTransitioningColor) {
            const colorsAttr = geometry.attributes.color;
            let finished = true;
            for (let i = 0; i < particlesCount * 3; i++) {
                const current = colorsAttr.array[i];
                const target = targetColors[i];
                const diff = target - current;
                if (Math.abs(diff) > 0.01) {
                    colorsAttr.array[i] += diff * lerpSpeed;
                    finished = false;
                } else {
                    colorsAttr.array[i] = target;
                }
            }
            colorsAttr.needsUpdate = true;
            if (finished) isTransitioningColor = false;
        }

        // Subtle camera swaying
        camera.position.x += (mouseX * 0.5 - camera.position.x) * 0.05;
        camera.position.y += (-mouseY * 0.5 - camera.position.y) * 0.05;
        camera.lookAt(scene.position);

        renderer.render(scene, camera);
        requestAnimationFrame(animate);
    };

    animate();

    // --- RESIZE ---
    window.addEventListener('resize', () => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
    });

    // --- EXPOSE CONTROLLER ---
    window.Orb = {
        updateConfig: (config) => {
            // Config: { palette: ['#hex',...], speed: float, amp: float }
            if (config.palette && config.palette.length > 0) {
                // Convert strings to Colors
                const newPalette = config.palette.map(c => new THREE.Color(c));

                // Assign new target colors
                for (let i = 0; i < particlesCount; i++) {
                    const i3 = i * 3;
                    const color = newPalette[Math.floor(Math.random() * newPalette.length)];
                    targetColors[i3] = color.r;
                    targetColors[i3 + 1] = color.g;
                    targetColors[i3 + 2] = color.b;
                }
                isTransitioningColor = true;
            }

            if (config.speed !== undefined) targetSpeed = config.speed;
            if (config.amp !== undefined) targetAmp = config.amp;
        }
    };
}
